/**
 * Text Animation Component
 * Handles scroll-triggered animations for text elements with paragraph-level staggering
 */

class TextAnimations {
    constructor() {
        this.animatedElements = document.querySelectorAll('.animate-on-scroll');
        this.observer = null;
        this.init();
    }

    init() {
        if (this.animatedElements.length === 0) return;

        // Process each animated element to split paragraphs
        this.animatedElements.forEach((element) => {
            this.processParagraphs(element);
        });

        // Create intersection observer
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.animateElement(entry.target);
                        // Stop observing this element once it's animated
                        this.observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        // Start observing all animated elements
        this.animatedElements.forEach((element) => {
            this.observer.observe(element);
        });
    }

    processParagraphs(element) {
        // Find all paragraphs, headings, and other block elements within the animated element
        const blockElements = element.querySelectorAll('p, h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6, .h7, .display-xl, .display-l, .display-m, blockquote, .button, .btn');
        
        if (blockElements.length === 0) return;

        // Add animation classes to each block element
        blockElements.forEach((block, index) => {
            block.classList.add('animate-paragraph');
            block.style.setProperty('--animation-delay', `${index * 0.25}s`);
        });

        // Mark this element as processed
        element.classList.add('paragraphs-processed');
    }

    animateElement(element) {
        if (element.classList.contains('paragraphs-processed')) {
            // Animate paragraphs individually
            const paragraphs = element.querySelectorAll('.animate-paragraph');
            paragraphs.forEach((paragraph) => {
                paragraph.classList.add('animate-in');
            });
        } else {
            // Fallback: animate the whole element
            element.classList.add('animate-in');
        }
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

// Initialize when DOM is ready
function initTextAnimations() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            new TextAnimations();
        });
    } else {
        new TextAnimations();
    }
}

// Initialize immediately
initTextAnimations();

// Re-initialize when new content is loaded (for AJAX sections)
document.addEventListener('shopify:section:load', () => {
    new TextAnimations();
});

export default TextAnimations;